/**
 * Skeleton Loading Components
 * 
 * Provides visual feedback during content loading for better perceived performance.
 */

import { memo } from 'react';

interface SkeletonProps {
    className?: string;
}

/**
 * Base skeleton component - animated gray rectangle
 */
function SkeletonComponent({ className = '' }: SkeletonProps) {
    return (
        <div
            className={`animate-pulse bg-gray-200 rounded ${className}`}
            aria-label="Loading..."
            role="status"
        />
    );
}

export const Skeleton = memo(SkeletonComponent);

/**
 * Chart skeleton - mimics chart loading state
 */
function ChartSkeletonComponent() {
    return (
        <div className="space-y-3 p-4" role="status" aria-label="Loading chart...">
            {/* Chart bars */}
            <div className="flex items-end justify-between gap-2 h-64">
                {[40, 65, 55, 80, 45, 70, 60, 75, 50, 85, 55, 70].map((height, i) => (
                    <div
                        key={i}
                        className="flex-1 bg-gray-200 animate-pulse rounded-t"
                        style={{ height: `${height}%` }}
                    />
                ))}
            </div>
            {/* Chart labels */}
            <div className="flex justify-between gap-2">
                {Array.from({ length: 12 }).map((_, i) => (
                    <Skeleton key={i} className="flex-1 h-4" />
                ))}
            </div>
        </div>
    );
}

export const ChartSkeleton = memo(ChartSkeletonComponent);

/**
 * Expense card skeleton - mimics expense card loading
 */
function ExpenseCardSkeletonComponent() {
    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <div className="flex items-start justify-between mb-3">
                <div className="flex-1 space-y-2">
                    <Skeleton className="h-6 w-32" />
                    <Skeleton className="h-4 w-24" />
                </div>
                <Skeleton className="h-8 w-24" />
            </div>
            <div className="flex items-center gap-2">
                <Skeleton className="h-6 w-6 rounded-full" />
                <Skeleton className="h-4 w-40" />
            </div>
        </div>
    );
}

export const ExpenseCardSkeleton = memo(ExpenseCardSkeletonComponent);

/**
 * Category breakdown skeleton
 */
function CategoryBreakdownSkeletonComponent() {
    return (
        <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3 flex-1">
                            <Skeleton className="w-12 h-12 rounded-lg" />
                            <div className="space-y-2 flex-1">
                                <Skeleton className="h-5 w-32" />
                                <Skeleton className="h-4 w-48" />
                            </div>
                        </div>
                        <div className="space-y-2 text-right">
                            <Skeleton className="h-6 w-24" />
                            <Skeleton className="h-4 w-16" />
                        </div>
                    </div>
                    <Skeleton className="h-3 w-full rounded-full" />
                </div>
            ))}
        </div>
    );
}

export const CategoryBreakdownSkeleton = memo(CategoryBreakdownSkeletonComponent);

/**
 * List skeleton - for expense lists
 */
interface ListSkeletonProps {
    count?: number;
}

function ListSkeletonComponent({ count = 5 }: ListSkeletonProps) {
    return (
        <div className="space-y-3">
            {Array.from({ length: count }).map((_, i) => (
                <ExpenseCardSkeleton key={i} />
            ))}
        </div>
    );
}

export const ListSkeleton = memo(ListSkeletonComponent);

/**
 * Full page skeleton
 */
function PageSkeletonComponent() {
    return (
        <div className="min-h-screen bg-gray-50 p-4">
            <div className="max-w-4xl mx-auto space-y-6">
                {/* Header */}
                <div className="space-y-2">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-4 w-64" />
                </div>

                {/* Stats cards */}
                <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white rounded-lg p-4 space-y-2">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-8 w-32" />
                    </div>
                    <div className="bg-white rounded-lg p-4 space-y-2">
                        <Skeleton className="h-4 w-24" />
                        <Skeleton className="h-8 w-32" />
                    </div>
                </div>

                {/* Main content */}
                <div className="bg-white rounded-lg p-4">
                    <ChartSkeleton />
                </div>

                {/* List */}
                <ListSkeleton count={3} />
            </div>
        </div>
    );
}

export const PageSkeleton = memo(PageSkeletonComponent);
